import { Box } from "@mui/material";
import { dataGridColorParams } from "@platform/design-system";
import { CommentDialog } from "@platform/plan-grid-core";
import {
  ColDef,
  ColGroupDef,
  GetMainMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  MenuItemDef,
  SuppressKeyboardEventParams,
  themeQuartz,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AiRateFieldsMeta,
  BenefitPlan,
  BenefitPlanGroup,
  CarrierBenefitPlanGroup,
} from "../../types/models";
import {
  buildRateEditMenuItem,
  getPlanHeaderName,
  isAlternatePlan,
  shouldSuppressHeaderMenu,
} from "../../utils/plan-helpers";
import {
  enrollmentFlagStateOf,
  NO_ENROLLMENT_FLAGS,
} from "../../utils/enrollmentFlagLevel";
import { buildEnrollmentReview } from "../../utils/quotedEnrollmentReview";
import { scaleColumnDefWidths } from "../../utils/scaleColumnDefWidths";
import {
  useCopyRateSchedules,
  useReplaceProposalRateSchedules,
  useReplaceRateSchedules,
} from "../../hooks/useRates";
import { useRateReviewMenu } from "../../hooks/useRateReviewMenu";
import { DeleteRateStructureConfirmDialog } from "../DeleteRateStructureConfirmDialog";
import { PasteRatesConfirmDialog } from "../PasteRatesConfirmDialog";

import { EnrollmentFlagProvider } from "./EnrollmentFlagContext";
import {
  AiRateHeader,
  FocusableRateCellWrapper,
  IllustratedFromContext,
  RatePlanRenderer,
  RatesLabelCell,
  type IllustratedFromEntry,
} from "./renderers";

import type { AxiosInstance } from "axios";
import type { RefObject } from "react";

const MIN_WIDTH = 450;

const REVIEW_QUOTED_ENROLLMENT_LABEL = "Review Quoted Enrollment";

// Shared defaults: a fresh `[]` or `{}` per render would rebuild every column.
const NO_ALIGNED_GRIDS: RefObject<AgGridReact>[] = [];
const NO_BENEFIT_PLAN_GROUPS: BenefitPlanGroup[] = [];
const NO_CARRIER_GROUPS: CarrierBenefitPlanGroup[] = [];
const NO_NETWORKS: readonly string[] = [];
const NO_COLUMN_WIDTHS: Record<string, number> = {};
const NO_CARRIER_IDS: readonly string[] = [];

// Shared CSS constants to avoid duplication
const TRANSPARENT_IMPORTANT = "transparent !important";
const AG_BORDER_IMPORTANT =
  "1px solid var(--ag-border-color, #babfc7) !important";

export interface RatesGridProps {
  /** Refs to other AG Grid instances to align with (e.g., SummaryGrid) */
  alignedGrids?: RefObject<AgGridReact>[];
  /** Offer the validated-flag actions on a rate cell's right-click menu. */
  enableCellValidation?: boolean;
  /** Benefit plan groups to display as columns */
  benefitPlanGroups?: BenefitPlanGroup[];
  /** Optional carrier-level grouping to mirror PlanEditor carrier super-groups */
  carrierGroups?: CarrierBenefitPlanGroup[];
  /** Open the RatesDrawer */
  onOpenDrawer?: (context: {
    benefitPlanGroup: BenefitPlanGroup;
    benefitPlan: BenefitPlan;
    /**
     * True when the column is a rate-sets carrier (see `rateSetsCarrierIds`) —
     * i.e. the Incumbent/Current plan, whose drawer exposes renewal / revised
     * renewal rate entry. False for ordinary carrier columns.
     */
    enableRateSets: boolean;
  }) => void;
  /** Axios instance for API calls */
  axiosInstance?: AxiosInstance;
  /** Callback when grid is ready, exposes grid API for alignment */
  onGridReady?: (api: GridApi) => void;
  /** Coverage type from URL (e.g., fmla, dental, vision) for LOC-specific behavior */
  coverageType?: string;
  /**
   * Callback fired after a rate mutation succeeds. Receives the id of the
   * benefit plan group the edited rate belongs to so the caller can refresh
   * just that group (e.g. its premiums) rather than all groups.
   */
  onRateSaved: (benefitPlanGroupId?: string) => void;
  /** Combined AI extraction metadata for rate-area fields, keyed by plan UUID */
  aiFieldsByPlanId?: Record<string, AiRateFieldsMeta>;
  /** Scales plan-column widths (1 = default) to match the PlanDesigner width preset. */
  columnWidthScale?: number;
  /**
   * Network sub-column names for network-dependent coverages (e.g. Dental PPO).
   * When provided, each plan column is split into matching sub-columns so
   * alignedGrids can sync widths with PlanEditor's network column structure.
   * Example: ['inNetwork', 'outOfNetwork']
   */
  networks?: readonly string[];
  /**
   * Column widths keyed by colId, sourced from the PlanEditor grid.
   * Applied directly to plan columns so RatesGrid tracks PlanEditor widths
   * without relying solely on alignedGrids colId matching.
   * For Dental PPO plans (single column vs two sub-columns in PlanEditor),
   * the inNetwork + outOfNetwork widths are summed automatically.
   */
  columnWidths?: Record<string, number>;
  /**
   * Carrier ids whose rates are read-only and whose column header menu is
   * hidden — e.g. the synthetic Incumbent/Requested carrier on the Quotes
   * SBS grid, which has no rates to edit.
   */
  readOnlyCarrierIds?: readonly string[];
  /**
   * Carrier ids whose column header menu opens the drawer in rate-sets mode —
   * the Incumbent/Current plan on the Quotes SBS grid, where renewal / revised
   * renewal rates are added. These columns show a header menu even when
   * read-only. Opt-in and unused by the Build tab, so its behavior is unchanged.
   */
  rateSetsCarrierIds?: readonly string[];
  /**
   * Plan ids with no backing proposal_plan row yet (pad/placeholder plans on
   * the Quotes SBS grid — see PlaceholderMaterializeTarget/PadFillTarget in
   * @platform/mpx-quotes-grid). Their header menu shows the edit action
   * disabled with `disabledPlanTooltip` instead of opening the drawer, since
   * BTX rejects a rate-schedule PUT against their synthetic FE id.
   */
  disabledPlanIds?: ReadonlySet<string>;
  /** Tooltip shown on the disabled menu item for a plan in `disabledPlanIds`. */
  disabledPlanTooltip?: string;
  /**
   * The SBS page's "illustrated from" entry. When provided, the Rates row's
   * left cell carries an editable input for it under the "Rates" label. Omit to
   * render the label alone.
   */
  illustratedFrom?: IllustratedFromEntry;
  /**
   * Opens the review of a quote's enrollment against Current's for the plan group. When
   * provided, a quoted plan's header menu offers "Review Quoted Enrollment" wherever the
   * carrier quoted enrollment, whether or not it is flagged. Omit it where no decision
   * can be recorded, such as the Build tab.
   */
  onReviewQuotedEnrollment?: (group: BenefitPlanGroup) => void;
}

/**
 * RatesGrid component that aligns with PlanDesigner grid columns
 *
 * Each column represents a Benefit Plan Group.
 * The column header menu opens the RatesDrawer for that group.
 */
export const RatesGrid = forwardRef<AgGridReact, RatesGridProps>(
  (
    {
      alignedGrids = NO_ALIGNED_GRIDS,
      benefitPlanGroups = NO_BENEFIT_PLAN_GROUPS,
      carrierGroups = NO_CARRIER_GROUPS,
      onOpenDrawer,
      axiosInstance,
      onGridReady: onGridReadyProp,
      coverageType,
      onRateSaved,
      aiFieldsByPlanId,
      columnWidthScale = 1,
      networks = NO_NETWORKS,
      columnWidths = NO_COLUMN_WIDTHS,
      readOnlyCarrierIds = NO_CARRIER_IDS,
      rateSetsCarrierIds = NO_CARRIER_IDS,
      disabledPlanIds,
      disabledPlanTooltip,
      enableCellValidation = false,
      illustratedFrom,
      onReviewQuotedEnrollment,
    },
    ref,
  ) => {
    const gridRef = useRef<AgGridReact>(null);

    // Expose the grid ref to parent via forwardRef
    useImperativeHandle(ref, () => gridRef.current as AgGridReact);

    // Note: gridApi is stored for potential future use
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_gridApi, setGridApi] = useState<GridApi | null>(null);

    // Clipboard state for copy/paste rates
    const [clipboard, setClipboard] = useState<{
      sourceBenefitPlanId: string;
      sourcePlanLabel: string;
      sourcePlanType: string;
    } | null>(null);
    const [pasteTarget, setPasteTarget] = useState<{
      destinationBenefitPlanId: string;
      destinationPlanLabel: string;
      destinationPlanType: string;
    } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{
      plan: BenefitPlan;
      benefitPlanGroupId: string;
    } | null>(null);

    const { getContextMenuItems, commentDialogProps } =
      useRateReviewMenu(enableCellValidation);

    const copyRatesMutation = useCopyRateSchedules(axiosInstance, {
      onSuccess: () => {
        setPasteTarget(null);
      },
    });

    const handleRateStructureDeleted = (): void => {
      onRateSaved(deleteTarget?.benefitPlanGroupId);
      setDeleteTarget(null);
    };
    const deleteRateSchedulesMutation = useReplaceRateSchedules(
      axiosInstance as AxiosInstance,
      { onSuccess: handleRateStructureDeleted },
    );
    const deleteProposalRateSchedulesMutation = useReplaceProposalRateSchedules(
      axiosInstance as AxiosInstance,
      { onSuccess: handleRateStructureDeleted },
    );

    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        setGridApi(params.api);
        onGridReadyProp?.(params.api);
      },
      [onGridReadyProp],
    );

    // Every plan group in either the flat or the carrier-grouped shape, once
    // per id.
    const benefitPlanGroupById = useMemo(() => {
      const map = new Map<string, BenefitPlanGroup>();
      for (const group of benefitPlanGroups) {
        map.set(group.id, group);
      }
      for (const carrierGroup of carrierGroups) {
        for (const group of carrierGroup.benefitPlanGroups) {
          map.set(group.id, group);
        }
      }
      return map;
    }, [benefitPlanGroups, carrierGroups]);

    // Every plan in this coverage across all groups, offered as age-banded copy
    // destinations so rates can be copied between plans in different groups.
    const allCoveragePlans = useMemo(
      () =>
        Array.from(benefitPlanGroupById.values()).flatMap(
          (group) => group.benefitPlans,
        ),
      [benefitPlanGroupById],
    );

    /**
     * Cell renderer that renders a single benefit plan based on its rate design
     * Wrapped in FocusableRateCellWrapper to handle focus delegation
     */
    const RatesCellRenderer = useCallback(
      (
        params: ICellRendererParams & {
          plan?: BenefitPlan;
          benefitPlanGroupId?: string;
          benefitPlanGroup?: BenefitPlanGroup;
          copyDestinationPlans?: BenefitPlan[];
          readOnly?: boolean;
        },
      ) => {
        const { colDef, eGridCell } = params;
        const field = colDef?.field;

        // Skip the left column (rateDesign)
        if (field === "rateDesign") {
          return (params.data as Record<string, string> | undefined)?.[
            "rateDesign"
          ];
        }

        // Get plan from cellRendererParams
        const plan: BenefitPlan | undefined = params.plan;

        if (!plan) {
          return null;
        }

        // Plans with "alternate" policyPeriod don't have rates - show empty cell for alignment
        if (isAlternatePlan(plan)) {
          return null;
        }

        // Tell the parent which group was edited so it can refresh just that
        // group's premiums rather than every group's (XTRM-2024). The deep
        // renderers keep their zero-arg onRateSaved; the group id is bound here.
        const benefitPlanGroupId = params.benefitPlanGroupId;
        const handleRateSaved = (): void => onRateSaved(benefitPlanGroupId);

        // Use RatePlanRenderer to render based on this plan's specific rate design
        // Wrapped in FocusableRateCellWrapper to handle cell focus → first input focus
        return (
          <FocusableRateCellWrapper eGridCell={eGridCell}>
            <EnrollmentFlagProvider
              state={
                params.benefitPlanGroup
                  ? enrollmentFlagStateOf(params.benefitPlanGroup, plan)
                  : NO_ENROLLMENT_FLAGS
              }
            >
              <RatePlanRenderer
                plan={plan}
                axiosInstance={axiosInstance}
                coverageType={coverageType}
                onRateSaved={handleRateSaved}
                readOnly={params.readOnly}
                benefitPlanGroup={params.benefitPlanGroup}
                copyDestinationPlans={params.copyDestinationPlans}
              />
            </EnrollmentFlagProvider>
          </FocusableRateCellWrapper>
        );
      },
      // A new renderer identity makes AG Grid remount every rate cell, which
      // drops the focused input mid-entry. Anything a refetch replaces reaches
      // the renderer through cellRendererParams instead, which AG Grid hands to
      // the mounted cell.
      [axiosInstance, coverageType, onRateSaved],
    );

    // Returns explicit width props sourced from PlanEditor when available.
    // For Dental PPO plans, RatesGrid has one column but PlanEditor has two
    // sub-columns (inNetwork + outOfNetwork) — sum them to get the full width.
    // minWidth is pinned to the same value: the column's default minWidth
    // (MIN_WIDTH × scale) can round 1px larger than PlanEditor's two summed
    // sub-columns, and AG Grid would otherwise clamp the explicit width back up.
    const resolveColumnWidth = useCallback(
      (
        colId: string,
      ):
        | { width: number; minWidth: number; flex: 0 }
        | Record<string, never> => {
        if (columnWidths[colId] !== undefined) {
          const width = columnWidths[colId];
          return { width, minWidth: width, flex: 0 };
        }
        const inNet = columnWidths[`${colId}.inNetwork`];
        const outNet = columnWidths[`${colId}.outOfNetwork`];
        if (inNet !== undefined && outNet !== undefined) {
          const width = inNet + outNet;
          return { width, minWidth: width, flex: 0 };
        }
        return {};
      },
      [columnWidths],
    );

    // Define the left column to match PlanDesigner's benefits column width
    const leftColumn: ColDef = useMemo(
      () => ({
        colId: "benefit.label",
        field: "rateDesign",
        headerName: "",
        pinned: "left",
        flex: 0,
        width: 200,
        // Matches the PlanEditor benefits column's minWidth so aligned-grid
        // width sync is not clamped when that column is dragged narrower.
        minWidth: 100,
        suppressMovable: true,
        lockPosition: "left",
        editable: false,
        suppressHeaderMenuButton: true,
        suppressNavigable: true,
        cellRenderer: RatesLabelCell,
        // The "illustrated from" input can outgrow a short rate table.
        autoHeight: true,
        // Keys typed into that input belong to it, not to grid navigation.
        suppressKeyboardEvent: ({ event }: SuppressKeyboardEventParams) =>
          event.target instanceof HTMLTextAreaElement,
      }),
      [],
    );

    const allBenefitPlanGroups = useMemo(
      () =>
        carrierGroups.length > 0
          ? carrierGroups.flatMap(
              (carrierGroup) => carrierGroup.benefitPlanGroups,
            )
          : benefitPlanGroups,
      [benefitPlanGroups, carrierGroups],
    );

    // Create a column group for each benefit plan group with sub-columns for each benefit plan
    const groupColumns: ColDef[] = useMemo(
      () =>
        benefitPlanGroups.map((group, index) => {
          const headerName =
            group.nickname || `Plan Group ${group.position || index + 1}`;

          const children: ColDef[] = group.benefitPlans.flatMap(
            (plan): ColDef[] => {
              const planHeader = getPlanHeaderName(plan);
              const suppressMenu = shouldSuppressHeaderMenu(plan);

              if (networks.length > 0) {
                // For network-dependent coverages (e.g. Dental PPO), split each plan into
                // sub-columns matching PlanEditor's colId pattern so alignedGrids can sync.
                // The renderer sits on the first sub-column and spans all network columns;
                // the remaining sub-columns are structural spacers for alignment only.
                const subWidth = MIN_WIDTH / networks.length;
                const lastNetworkIndex = networks.length - 1;
                return networks.map((network, networkIndex) => ({
                  colId: `${plan.identifier}.${network}`,
                  field: plan.id,
                  headerName: networkIndex === 0 ? planHeader : "",
                  headerComponentParams:
                    networkIndex === 0
                      ? {
                          innerHeaderComponent: AiRateHeader,
                          innerHeaderComponentParams: {
                            aiFields: aiFieldsByPlanId?.[plan.id],
                            axiosInstance,
                          },
                        }
                      : undefined,
                  headerClass:
                    networkIndex === lastNetworkIndex
                      ? undefined
                      : "rates-network-spacer-header",
                  flex: 1,
                  minWidth: subWidth,
                  editable: false,
                  autoHeight: networkIndex === 0,
                  wrapText: networkIndex === 0,
                  cellRenderer:
                    networkIndex === 0 ? RatesCellRenderer : undefined,
                  cellRendererParams:
                    networkIndex === 0
                      ? {
                          plan,
                          benefitPlanGroupId: group.id,
                          benefitPlanGroup: group,
                          copyDestinationPlans: allCoveragePlans,
                        }
                      : undefined,
                  colSpan:
                    networkIndex === 0 ? () => networks.length : undefined,
                  suppressHeaderMenuButton:
                    networkIndex === lastNetworkIndex ? suppressMenu : true,
                }));
              }

              return [
                {
                  colId: plan.identifier,
                  field: plan.id,
                  headerName: planHeader,
                  headerComponentParams: {
                    innerHeaderComponent: AiRateHeader,
                    innerHeaderComponentParams: {
                      aiFields: aiFieldsByPlanId?.[plan.id],
                      axiosInstance,
                    },
                  },
                  flex: 1,
                  minWidth: MIN_WIDTH,
                  editable: false,
                  autoHeight: true,
                  wrapText: true,
                  cellRenderer: RatesCellRenderer,
                  cellRendererParams: {
                    plan,
                    benefitPlanGroupId: group.id,
                    benefitPlanGroup: group,
                    copyDestinationPlans: allCoveragePlans,
                  },
                  suppressHeaderMenuButton: suppressMenu,
                },
              ];
            },
          );

          return {
            groupId: `group-${group.id}`,
            headerName,
            marryChildren: true,
            children,
            headerGroupComponent: undefined,
            suppressHeaderMenuButton: false,
          };
        }),
      [
        benefitPlanGroups,
        RatesCellRenderer,
        networks,
        aiFieldsByPlanId,
        axiosInstance,
        allCoveragePlans,
      ],
    );

    const carrierGroupedColumns: ColDef[] = useMemo(
      () =>
        carrierGroups.map((carrierGroup) => {
          // Mirror PlanEditor's per-carrier network logic: only PPO carriers get
          // inNetwork/outOfNetwork sub-columns. Non-PPO carriers in the same dental
          // PPO RFP get a single plan column so colIds match across alignedGrids.
          const carrierIsPPO =
            carrierGroup.benefitPlanGroups[0]?.productType === "ppo";
          const carrierNetworks = carrierIsPPO ? networks : [];
          const isReadOnlyCarrier = readOnlyCarrierIds.includes(
            carrierGroup.carrierId,
          );
          // Rate-sets carriers (Incumbent/Current) keep a header menu even when
          // read-only, so the analyst can open the drawer to add renewal rates.
          const isRateSetsCarrier = rateSetsCarrierIds.includes(
            carrierGroup.carrierId,
          );

          return {
            headerName: carrierGroup.carrierName,
            groupId: `carrier-${carrierGroup.groupKey ?? carrierGroup.carrierId}`,
            marryChildren: false,
            children: carrierGroup.benefitPlanGroups.map((group, index) => {
              const headerName =
                group.nickname || `Plan Group ${group.position || index + 1}`;

              const children: ColDef[] = group.benefitPlans.flatMap(
                (plan): ColDef[] => {
                  const planHeader = getPlanHeaderName(plan);
                  const suppressMenu = shouldSuppressHeaderMenu(plan);

                  if (carrierNetworks.length > 0) {
                    const subWidth = MIN_WIDTH / carrierNetworks.length;
                    const lastNetworkIndex = carrierNetworks.length - 1;
                    return carrierNetworks.map((network, networkIndex) => ({
                      // colId keyed off plan.id (matching PlanEditor's carrier-grouped
                      // colId) so alignedGrids stay in sync after PlanEditor switched
                      // its network colId to plan.id.
                      colId: `${plan.id}.${network}`,
                      field: plan.id,
                      headerName: networkIndex === 0 ? planHeader : "",
                      headerComponentParams:
                        networkIndex === 0
                          ? {
                              innerHeaderComponent: AiRateHeader,
                              innerHeaderComponentParams: {
                                aiFields: aiFieldsByPlanId?.[plan.id],
                                axiosInstance,
                              },
                            }
                          : undefined,
                      headerClass:
                        networkIndex === lastNetworkIndex
                          ? undefined
                          : "rates-network-spacer-header",
                      minWidth: subWidth,
                      editable: false,
                      autoHeight: networkIndex === 0,
                      wrapText: networkIndex === 0,
                      cellRenderer:
                        networkIndex === 0 ? RatesCellRenderer : undefined,
                      cellRendererParams:
                        networkIndex === 0
                          ? {
                              plan,
                              benefitPlanGroupId: group.id,
                              benefitPlanGroup: group,
                              copyDestinationPlans: allCoveragePlans,
                              readOnly: isReadOnlyCarrier,
                            }
                          : undefined,
                      colSpan:
                        networkIndex === 0
                          ? () => carrierNetworks.length
                          : undefined,
                      // Menu on the last sub-column so it sits at the plan's right
                      // edge rather than mid-plan at an internal sub-column border.
                      suppressHeaderMenuButton:
                        networkIndex === lastNetworkIndex
                          ? suppressMenu ||
                            (isReadOnlyCarrier && !isRateSetsCarrier)
                          : true,
                    }));
                  }

                  return [
                    {
                      // Keyed off plan.id (matching PlanEditor's carrier-grouped
                      // colId) rather than plan.identifier: identifiers are RFP
                      // plan labels ("CI-1-1") repeated across carriers, so they
                      // collide in a side-by-side grid.
                      colId: plan.id,
                      field: plan.id,
                      headerName: planHeader,
                      headerComponentParams: {
                        innerHeaderComponent: AiRateHeader,
                        innerHeaderComponentParams: {
                          aiFields: aiFieldsByPlanId?.[plan.id],
                          axiosInstance,
                        },
                      },
                      minWidth: MIN_WIDTH,
                      editable: false,
                      autoHeight: true,
                      wrapText: true,
                      cellRenderer: RatesCellRenderer,
                      cellRendererParams: {
                        plan,
                        benefitPlanGroupId: group.id,
                        benefitPlanGroup: group,
                        copyDestinationPlans: allCoveragePlans,
                        readOnly: isReadOnlyCarrier,
                      },
                      suppressHeaderMenuButton:
                        suppressMenu ||
                        (isReadOnlyCarrier && !isRateSetsCarrier),
                    },
                  ];
                },
              );

              return {
                groupId: `group-${group.id}`,
                headerName,
                marryChildren: true,
                children,
                headerGroupComponent: undefined,
                suppressHeaderMenuButton: false,
              };
            }),
          };
        }),
      [
        carrierGroups,
        RatesCellRenderer,
        networks,
        readOnlyCarrierIds,
        rateSetsCarrierIds,
        aiFieldsByPlanId,
        axiosInstance,
        allCoveragePlans,
      ],
    );

    // Plan ids that belong to a rate-sets carrier (Incumbent/Current), so the
    // header menu can open the drawer in renewal rate-sets mode for them.
    const rateSetsPlanIds = useMemo(
      () =>
        new Set(
          carrierGroups
            .filter((carrierGroup) =>
              rateSetsCarrierIds.includes(carrierGroup.carrierId),
            )
            .flatMap((carrierGroup) => carrierGroup.benefitPlanGroups)
            .flatMap((group) => group.benefitPlans)
            .map((plan) => plan.id),
        ),
      [carrierGroups, rateSetsCarrierIds],
    );

    // Apply PlanEditor column widths after scaling so the already-rendered pixel
    // widths from PlanEditor are not scaled a second time by scaleColumnDefWidths.
    const applyWidthOverrides = useCallback(
      (defs: (ColDef | ColGroupDef)[]): (ColDef | ColGroupDef)[] =>
        defs.map((def) => {
          if ("children" in def) {
            return { ...def, children: applyWidthOverrides(def.children) };
          }
          if (!def.colId) return def;
          // Match on colId first, then fall back to field (the plan id) for
          // columns whose colId does not match how PlanEditor keys its own.
          let override = resolveColumnWidth(def.colId);
          if (Object.keys(override).length === 0 && def.field) {
            override = resolveColumnWidth(def.field);
          }
          return Object.keys(override).length > 0
            ? { ...def, ...override }
            : def;
        }),
      [resolveColumnWidth],
    );

    // Memoized because AG Grid re-applies columnDefs on every new array
    // identity: with autoHeight that tears down and remeasures every cell,
    // which reads as the grid expanding then snapping back.
    const columnDefs = useMemo(
      () =>
        applyWidthOverrides(
          scaleColumnDefWidths(
            [
              leftColumn,
              ...(carrierGroups.length > 0
                ? carrierGroupedColumns
                : groupColumns),
            ],
            columnWidthScale,
          ),
        ),
      [
        applyWidthOverrides,
        leftColumn,
        carrierGroups.length,
        carrierGroupedColumns,
        groupColumns,
        columnWidthScale,
      ],
    );

    const defaultColDef: ColDef = useMemo(
      () => ({
        flex: 1,
        minWidth: MIN_WIDTH, // Consistent width for aligned grid sync with PlanDesigner/SummaryGrid
        resizable: false,
        sortable: false,
        filter: false,
        editable: false,
        // Handle keyboard events inside custom cell renderers.
        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
          const { event } = params;

          // When an <input> inside the cell has focus, suppress all arrow keys
          // so AG Grid does not intercept them. ArrowLeft/Right are handled by
          // the browser (cursor movement); ArrowUp/Down are handled by
          // DebouncedNumericInput's grid-navigation logic.
          if (
            event.target instanceof HTMLInputElement &&
            (event.key === "ArrowLeft" ||
              event.key === "ArrowRight" ||
              event.key === "ArrowUp" ||
              event.key === "ArrowDown")
          ) {
            return true;
          }

          // Suppress Tab so DebouncedNumericInput can move focus between inputs
          // within the cell; AG Grid Tab handling would jump to a different cell.
          if (event.key !== "Tab") {
            return false;
          }
          return true;
        },
      }),
      [],
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: themeQuartz.withParams(dataGridColorParams),
        domLayout: "autoHeight",
        suppressHorizontalScroll: true,
        // Hide the column group header row (plan group names) - redundant with coverage grid
        groupHeaderHeight: 0,
        // Reduce header height to minimize spacing
        headerHeight: 28,
        // Enable keyboard navigation into the grid
        tabIndex: 3, // Tab order after PlanDesigner
        // Suppress header focus since we want focus to go to cell inputs
        suppressHeaderFocus: true,
        // Suppress cell focus to allow focus to go directly to inputs inside cells
        suppressCellFocus: false,
        // Shortens the hover delay before the disabled-menu-item tooltip
        // (explaining why rates can't be edited yet) appears.
        tooltipShowDelay: 1000,
      }),
      [],
    );

    // Builds the header-menu items for a single plan column. The rate-sets
    // (Incumbent/Current) column only offers the drawer entry — Delete/Copy/Paste would
    // re-enable editing its otherwise read-only rates.
    const buildPlanMenuItems = useCallback(
      (plan: BenefitPlan, group: BenefitPlanGroup): MenuItemDef[] => {
        const planType =
          plan.rateScheduleSource === "proposal" ? "proposal" : "benefitPlan";
        const isRateSetsPlan = rateSetsPlanIds.has(plan.id);
        const isDisabledPlan = disabledPlanIds?.has(plan.id) ?? false;

        const editMenuItem = buildRateEditMenuItem({
          isRateSetsPlan,
          isDisabled: isDisabledPlan,
          tooltip: disabledPlanTooltip,
          action: () => {
            onOpenDrawer?.({
              benefitPlanGroup: group,
              benefitPlan: plan,
              enableRateSets: isRateSetsPlan,
            });
          },
        });

        if (isDisabledPlan) {
          return [editMenuItem];
        }

        const items: MenuItemDef[] = [editMenuItem];

        if (isRateSetsPlan) {
          return items;
        }

        // Offered wherever the carrier quoted enrollment, flagged or not, so any analyst can
        // check a quote against Current's.
        if (
          onReviewQuotedEnrollment &&
          plan.rateScheduleSource === "proposal" &&
          buildEnrollmentReview(group)
        ) {
          items.push({
            name: REVIEW_QUOTED_ENROLLMENT_LABEL,
            action: () => onReviewQuotedEnrollment(group),
          });
        }

        items.push({
          name: "Delete Rate Structure",
          action: () => {
            setDeleteTarget({ plan, benefitPlanGroupId: group.id });
          },
        });
        items.push("separator" as unknown as MenuItemDef);
        items.push({
          name: "Copy Rates",
          action: () => {
            setClipboard({
              sourceBenefitPlanId: plan.id,
              sourcePlanLabel: getPlanHeaderName(plan),
              sourcePlanType: planType,
            });
          },
        });

        if (clipboard && clipboard.sourceBenefitPlanId !== plan.id) {
          items.push({
            name: `Paste Rates (from ${clipboard.sourcePlanLabel})`,
            action: () => {
              setPasteTarget({
                destinationBenefitPlanId: plan.id,
                destinationPlanLabel: getPlanHeaderName(plan),
                destinationPlanType: planType,
              });
            },
          });
        }

        return items;
      },
      [
        rateSetsPlanIds,
        onOpenDrawer,
        onReviewQuotedEnrollment,
        clipboard,
        disabledPlanIds,
        disabledPlanTooltip,
      ],
    );

    // Custom menu items for plan columns (field is the plan UUID); the left
    // "rateDesign" column and non-plan columns get no custom menu.
    const getMainMenuItems = useCallback(
      (params: GetMainMenuItemsParams) => {
        if (!params.column) {
          return params.defaultItems ?? [];
        }
        const field = params.column.getColDef().field;
        if (!field || field === "rateDesign") {
          return [];
        }

        for (const group of allBenefitPlanGroups) {
          const plan = group.benefitPlans.find((p) => p.id === field);
          if (plan) {
            return buildPlanMenuItems(plan, group);
          }
        }

        return [];
      },
      [allBenefitPlanGroups, buildPlanMenuItems],
    );

    // This grid renders exactly one row; the cell renderer reads live plan
    // data via cellRendererParams rather than this row, so the row's actual
    // content is just the set of plan ids. An unrelated benefit edit
    // elsewhere in the SBS grid rebuilds proposals (and thus
    // allBenefitPlanGroups) as a brand-new reference every time, which would
    // otherwise hand AG Grid a new rowData array with identical plan ids —
    // with domLayout "autoHeight" that's enough to make it remeasure every
    // wrapped cell, collapsing the row then snapping back to height (the
    // "flicker"). Keeping the same row reference when the plan id set hasn't
    // changed avoids that remeasure.
    const previousRowDataRef = useRef<[Record<string, string>] | []>([]);

    // Transform benefit plan groups into grid row data
    const computedRowData = useMemo(() => {
      if (allBenefitPlanGroups.length === 0) {
        previousRowDataRef.current = [];
        return previousRowDataRef.current;
      }

      // Create a single row with data for each benefit plan
      const row: Record<string, string> = {
        rateDesign: "Rates",
      };

      allBenefitPlanGroups.forEach((group) => {
        group.benefitPlans.forEach((plan) => {
          // Store plan id as value; cell renderer will access plan via cellRendererParams
          // Use plan.id as key (matching column field) for alignedGrids compatibility with PlanDesigner
          row[plan.id] = plan.id;
        });
      });

      const previousRow = previousRowDataRef.current[0];
      // Every value equals its own key (plan.id) or the static "Rates" label,
      // so comparing key sets is sufficient to detect an unchanged row.
      if (
        previousRow &&
        Object.keys(previousRow).length === Object.keys(row).length &&
        Object.keys(row).every((key) => key in previousRow)
      ) {
        return previousRowDataRef.current;
      }

      const nextRowData: [Record<string, string>] = [row];
      previousRowDataRef.current = nextRowData;
      return nextRowData;
    }, [allBenefitPlanGroups]);

    return (
      <>
        <Box
          className="rates-grid"
          data-grid-id="rates-grid"
          sx={{
            width: "100%",
            height: "auto",
            margin: 0,
            padding: 0,
            "& .ag-root-wrapper": {
              borderRadius: 0,
            },
            // Reduce header cell padding and add borders to match the cells below
            "& .ag-header-cell": {
              padding: "0 8px !important",
              borderTop: AG_BORDER_IMPORTANT,
              borderRight: AG_BORDER_IMPORTANT,
              borderBottom: AG_BORDER_IMPORTANT,
            },
            // First header cell gets left border to close off the row
            "& .ag-header-cell:first-of-type": {
              borderLeft: AG_BORDER_IMPORTANT,
            },
            "& .ag-header-cell.rates-network-spacer-header": {
              borderRight: "none !important",
            },
            // Remove all hover highlights
            "& .ag-row:hover, & .ag-row-hover, & .ag-row.ag-row-hover": {
              backgroundColor: TRANSPARENT_IMPORTANT,
              "--ag-row-hover-color": TRANSPARENT_IMPORTANT,
            },
            "& .ag-cell:hover, & .ag-cell-hover, & .ag-cell.ag-cell-range-selected":
              {
                backgroundColor: TRANSPARENT_IMPORTANT,
              },
            "&": {
              "--ag-row-hover-color": "transparent",
              "--ag-selected-row-background-color": "transparent",
              "--ag-range-selection-background-color": "transparent",
            },
            // Remove cell padding so child content is flush with borders
            "& .ag-cell": {
              padding: "0 !important",
              // Add borders on all sides of each cell
              borderRight: AG_BORDER_IMPORTANT,
              borderBottom: AG_BORDER_IMPORTANT,
            },
            // Ensure cell wrapper and content expand to fill full cell width
            "& .ag-cell-wrapper": {
              width: "100%",
            },
            "& .ag-cell-value": {
              width: "100%",
            },
            // First column gets left border
            '& .ag-cell:first-child, & .ag-cell[col-id="benefit.label"]': {
              borderLeft: AG_BORDER_IMPORTANT,
            },
            // Add left padding to the benefit label cell
            '& .ag-cell[col-id="benefit.label"]': {
              paddingLeft: "8px !important",
            },
          }}
        >
          <IllustratedFromContext.Provider value={illustratedFrom}>
            <AgGridReact
              ref={gridRef}
              rowData={computedRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
              suppressMovableColumns
              getMainMenuItems={getMainMenuItems}
              getContextMenuItems={getContextMenuItems}
              alignedGrids={alignedGrids}
            />
          </IllustratedFromContext.Provider>
        </Box>
        <CommentDialog {...commentDialogProps} />
        <PasteRatesConfirmDialog
          open={pasteTarget !== null}
          onCancel={() => setPasteTarget(null)}
          onConfirm={() => {
            if (!clipboard || !pasteTarget) return;
            copyRatesMutation.mutate({
              sourceBenefitPlanId: clipboard.sourceBenefitPlanId,
              sourcePlanType: clipboard.sourcePlanType,
              destinationBenefitPlanId: pasteTarget.destinationBenefitPlanId,
              destinationPlanType: pasteTarget.destinationPlanType,
            });
          }}
          sourcePlanLabel={clipboard?.sourcePlanLabel ?? ""}
          destinationPlanLabel={pasteTarget?.destinationPlanLabel ?? ""}
          isPasting={copyRatesMutation.isPending}
        />
        <DeleteRateStructureConfirmDialog
          open={deleteTarget !== null}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => {
            if (!deleteTarget) return;
            const planId = deleteTarget.plan.id;
            if (deleteTarget.plan.rateScheduleSource === "proposal") {
              deleteProposalRateSchedulesMutation.mutate({
                proposalPlanId: planId,
                rateSchedules: [],
              });
            } else {
              deleteRateSchedulesMutation.mutate({
                benefitPlanId: planId,
                rateSchedules: [],
              });
            }
          }}
          planLabel={deleteTarget ? getPlanHeaderName(deleteTarget.plan) : ""}
          isDeleting={
            deleteRateSchedulesMutation.isPending ||
            deleteProposalRateSchedulesMutation.isPending
          }
        />
      </>
    );
  },
);

RatesGrid.displayName = "RatesGrid";

export default RatesGrid;
